import { Suspense, lazy, useEffect, useState } from 'react'
import { CalendarRange, ClipboardList, Columns3, History, Home, List, Mail, Moon, Network, Newspaper, Package, ShieldCheck, Sun, UserCheck } from 'lucide-react'
import { ScopedTabs, type TabDef } from '@/components/ui/ScopedTabs'
import { ErrorBoundary } from '@/components/ui/ErrorBoundary'
import { Badge, Button, Card, Spinner } from '@/components/ui/primitives'
import { useAuth } from '@/lib/auth'
import { logoFor, useTheme } from '@/lib/theme'
import { NotificationBell } from '@/features/notifications/NotificationBell'
import { GlobalSearch, SearchButton } from '@/features/search/GlobalSearch'
import { useToast } from '@/components/ui/Toast'
import { BUILD, buildDate, shortLabel } from '@/lib/build'
import { trackTab } from '@/lib/analytics'
import { CLEARED_ITEM_IDS, tabForUrl, useUrlState } from '@/lib/urlState'
import { ROLE_LABELS } from '@/lib/capabilities'
import { PROJECT } from '@/data/project'
import { HomePage } from '@/features/home/HomePage'

// Each tab is its own chunk. Overview stays eager because it is what everyone lands on;
// making it lazy would only add a spinner to the first paint. The rest are fetched when
// first opened — Admin in particular carries the whole seed dataset and most people who
// sign in will never open it.
const StatusUpdatesPage = lazy(() =>
  import('@/features/status/StatusUpdatesPage').then((m) => ({ default: m.StatusUpdatesPage })))
const ActivityHistoryPage = lazy(() =>
  import('@/features/activity/ActivityHistoryPage').then((m) => ({ default: m.ActivityHistoryPage })))
const RoadmapPage = lazy(() =>
  import('@/features/roadmap/RoadmapPage').then((m) => ({ default: m.RoadmapPage })))
const DecisionsPage = lazy(() =>
  import('@/features/decisions/DecisionsPage').then((m) => ({ default: m.DecisionsPage })))
const FieldsPage = lazy(() =>
  import('@/features/fields/FieldsPage').then((m) => ({ default: m.FieldsPage })))
const PicklistsPage = lazy(() =>
  import('@/features/picklists/PicklistsPage').then((m) => ({ default: m.PicklistsPage })))
const ProductsPage = lazy(() =>
  import('@/features/products/ProductsPage').then((m) => ({ default: m.ProductsPage })))
const OwnersPage = lazy(() =>
  import('@/features/owners/OwnersPage').then((m) => ({ default: m.OwnersPage })))
const OrgChartPage = lazy(() =>
  import('@/features/orgchart/OrgChartPage').then((m) => ({ default: m.OrgChartPage })))
const MessagesPage = lazy(() =>
  import('@/features/messages/MessagesPage').then((m) => ({ default: m.MessagesPage })))
const AdminPage = lazy(() =>
  import('@/features/admin/AdminPage').then((m) => ({ default: m.AdminPage })))

function ThemeToggle() {
  const { theme, toggle } = useTheme()
  return (
    <Button
      onClick={toggle}
      aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
      title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
    >
      {theme === 'dark' ? <Sun size={15} aria-hidden /> : <Moon size={15} aria-hidden />}
    </Button>
  )
}

function Brand({ compact = false }: { compact?: boolean }) {
  const { theme } = useTheme()
  return (
    <div className="flex items-center gap-3">
      <img src={logoFor(theme)} alt="Milestone Consulting" className={compact ? 'h-7' : 'h-9'} />
      {!compact && (
        <div className="hidden border-l border-mc-line pl-3 sm:block">
          <p className="text-sm font-bold text-mc-primary">{PROJECT.client}</p>
          <p className="text-xs text-mc-muted">Salesforce Org Migration</p>
        </div>
      )}
    </div>
  )
}

function SignIn() {
  const { signIn, rejectedDomain, signInError } = useAuth()
  const { theme } = useTheme()
  const toast = useToast()

  // The card already shows these; the toast makes them impossible to miss on a phone,
  // where the message can sit below the fold.
  useEffect(() => {
    if (signInError) toast.error('Sign-in failed', signInError)
  }, [signInError, toast])
  useEffect(() => {
    if (rejectedDomain) {
      toast.warning(`${rejectedDomain} is not on this project`,
        'Use your Missional Marketing or Milestone Consulting account.')
    }
  }, [rejectedDomain, toast])
  return (
    <div className="flex min-h-dvh items-center justify-center p-4">
      <div className="absolute top-3 right-3"><ThemeToggle /></div>
      <Card className="w-full max-w-md">
        <div className="text-center">
          <img src={logoFor(theme)} alt="Milestone Consulting" className="mx-auto h-10" />
          <h1 className="mt-5 text-xl font-bold text-mc-primary">{PROJECT.client}</h1>
          <p className="mt-1 text-sm text-mc-muted">Salesforce Org Migration — delivery tracker</p>

          {rejectedDomain && (
            <p className="mt-4 rounded border border-mc-critical/30 bg-mc-critical-soft px-3 py-2 text-sm text-mc-critical">
              <strong>{rejectedDomain}</strong> is not on this project. Sign in with your
              Missional Marketing or Milestone Consulting account.
            </p>
          )}

          {signInError && (
            <p className="mt-4 rounded border border-mc-critical/30 bg-mc-critical-soft px-3 py-2 text-sm text-mc-critical">
              {signInError}
            </p>
          )}

          <Button variant="primary" className="mt-6 w-full" onClick={() => void signIn()}>
            Sign in with Google
          </Button>
          <p className="mt-4 text-xs text-mc-muted">
            Access is limited to missionalmarketing.com and milestoneconsulting.tech accounts.
          </p>
          <p className="mt-3 font-mono text-xs text-mc-muted">{shortLabel()}</p>
        </div>
      </Card>
    </div>
  )
}

export function App() {
  const { user, profile, role, isAdmin, can, loading, signOut } = useAuth()
  const { url, setUrl } = useUrlState()
  const [searchOpen, setSearchOpen] = useState(false)
  const tab = tabForUrl(url)
  const setTab = (next: string) =>
    // Switching tab by hand abandons whatever detail was deep-linked.
    setUrl({ tab: next, ...CLEARED_ITEM_IDS })

  useEffect(() => { if (user) trackTab(tab) }, [tab, user])

  if (loading && !user) return <div className="min-h-dvh"><Spinner label="Signing in" /></div>
  if (!user) return <SignIn />
  if (!profile) return <div className="min-h-dvh"><Spinner label="Loading your profile" /></div>

  if (profile.disabled) {
    return (
      <div className="flex min-h-dvh items-center justify-center p-4">
        <Card className="max-w-md text-center">
          <h1 className="text-lg font-bold text-mc-critical">Access suspended</h1>
          <p className="mt-2 text-sm text-mc-body">
            Your access to this tracker has been turned off. Contact Milestone Consulting.
          </p>
          <Button className="mt-4" onClick={() => void signOut()}>Sign out</Button>
        </Card>
      </div>
    )
  }

  const tabs: TabDef[] = [
    { id: 'home', label: 'Overview', shortLabel: 'Home', icon: <Home size={15} aria-hidden /> },
    // No capability gate. The weekly update is the summary of the project that gets read
    // aloud on the call, and `statusUpdates` in `firestore.rules` is open to everyone
    // signed in for the same reason. The money inside it is withheld separately.
    { id: 'status', label: 'Status Updates', shortLabel: 'Status', icon: <Newspaper size={15} aria-hidden /> },
    { id: 'roadmap', label: 'Roadmap', shortLabel: 'Plan', icon: <CalendarRange size={15} aria-hidden /> },
    ...(can('owners.view')
      ? [{ id: 'owners', label: 'Owners', shortLabel: 'Owners', icon: <UserCheck size={15} aria-hidden /> }]
      : []),
    // The one OR in the whole capability model, and it is here because this tab is three
    // readerships wearing one name: the diagram (`orgchart.view`), the staff roster
    // (`staff.view`) and the sharing rules table (`sharingrule.view`). Each sub-tab inside
    // is gated on its own single capability — `OrgChartPage` does that, and lands the
    // visitor on the first sub-tab they can actually see — so this OR decides only whether
    // there is anything behind the tab at all. Collapsing it back to `orgchart.view` would
    // hide the whole tab from somebody granted the sharing rules and nothing else.
    ...(can('orgchart.view') || can('staff.view') || can('sharingrule.view')
      ? [{ id: 'orgchart', label: 'Org Chart', shortLabel: 'Org', icon: <Network size={15} aria-hidden /> }]
      : []),
    ...(can('decisions.view')
      ? [{ id: 'decisions', label: 'Decisions', shortLabel: 'Items', icon: <ClipboardList size={15} aria-hidden /> }]
      : []),
    ...(can('fields.view')
      ? [{ id: 'fields', label: 'Field Guide', shortLabel: 'Fields', icon: <Columns3 size={15} aria-hidden /> }]
      : []),
    // Reviewing a dropdown's options is the same job as deciding the field it sits on, so
    // it is the same permission. The tab is separate because the work is: one is 928 fields
    // to keep or drop, the other is the options inside the ones being kept.
    ...(can('fields.view')
      ? [{ id: 'picklists', label: 'Picklists', shortLabel: 'Lists', icon: <List size={15} aria-hidden /> }]
      : []),
    ...(can('products.view')
      ? [{ id: 'products', label: 'Products', shortLabel: 'Products', icon: <Package size={15} aria-hidden /> }]
      : []),
    // Anyone who can open an item can see what was said on it, so the tab is offered
    // wherever there is anything at all to show.
    ...(can('activity.view')
      ? [{ id: 'activity', label: 'Activity History', shortLabel: 'Activity', icon: <History size={15} aria-hidden /> }]
      : []),
    // No capability gate, and deliberately none: a thread is read by the people on it
    // rather than by a role, so there is nothing for a capability to decide. Everybody
    // signed in can write to somebody; everybody sees only their own conversations.
    { id: 'messages', label: 'Messages', shortLabel: 'Messages', icon: <Mail size={15} aria-hidden /> },
    // Administering the tracker is Milestone's job — the client has no tab for it.
    ...(isAdmin
      ? [{ id: 'admin', label: 'Admin', shortLabel: 'Admin', icon: <ShieldCheck size={15} aria-hidden /> }]
      : []),
  ]

  return (
    <div className="min-h-dvh">
      {/* Mounted from the first paint so the shortcut works on every tab, but it loads
          nothing until somebody opens it. */}
      <GlobalSearch
        open={searchOpen}
        onOpenChange={setSearchOpen}
        destinations={tabs.map((t) => ({ id: t.id, label: t.label }))}
        onGoTo={setTab}
        onNavigate={setUrl}
      />

      <header className="sticky top-0 z-30 border-b border-mc-line bg-mc-surface">
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-3 py-2.5 sm:px-6">
          <Brand />
          <div className="ml-auto flex items-center gap-2">
            {isAdmin && <Badge tone="primary">Admin</Badge>}
            <div className="hidden text-right sm:block">
              <p className="text-xs font-medium text-mc-ink">{profile.displayName ?? profile.email}</p>
              <p className="text-xs text-mc-muted">{role ? ROLE_LABELS[role] : ''}</p>
            </div>
            <SearchButton onClick={() => setSearchOpen(true)} />
            <NotificationBell onNavigate={setUrl} />
            <ThemeToggle />
            <Button onClick={() => void signOut()}>Sign out</Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-3 py-4 sm:px-6 sm:py-6">
        <ScopedTabs tabs={tabs} activeId={tab} onChange={setTab}>
          {/* Keyed and reset by tab, so navigating away from a broken area clears it
              rather than leaving the error pinned to every tab you visit next. */}
          <ErrorBoundary area={tabs.find((t) => t.id === tab)?.label ?? tab} resetKeys={[tab]}>
          <Suspense fallback={<Spinner label="Loading" />}>
          {tab === 'home' && <HomePage onNavigate={setUrl} />}
          {tab === 'status' && (
            <StatusUpdatesPage
              openId={url.update}
              onOpenChange={(id) => setUrl({ update: id })}
            />
          )}
          {tab === 'roadmap' && (
            <RoadmapPage
              openId={url.task}
              onOpenChange={(id) => setUrl({ task: id })}
              // Crossing to the register closes the task drawer behind it: `tabForUrl`
              // reads a decision id as the Decisions tab, and leaving `task` set would
              // put the old drawer back the moment the person navigated back.
              onOpenDecision={(id) => setUrl({ decision: id, task: null })}
            />
          )}
          {tab === 'owners' && can('owners.view') && (
            <OwnersPage openId={url.owner} onOpenChange={(id) => setUrl({ owner: id })} />
          )}
          {/* The same three-way test the tab registry above uses, and for the same reason
              — see the comment there. `OrgChartPage` re-checks each one to decide which of
              its sub-tabs exist. */}
          {tab === 'orgchart' && (can('orgchart.view') || can('staff.view') || can('sharingrule.view')) && (
            <OrgChartPage
              view={url.view}
              person={url.person}
              container={url.container}
              note={url.note}
              sharingRule={url.sharingRule}
              onNavigate={setUrl}
            />
          )}
          {tab === 'decisions' && can('decisions.view') && (
            <DecisionsPage
              openId={url.decision}
              onOpenChange={(id) => setUrl({ decision: id })}
              onOpenTask={(id) => setUrl({ task: id, decision: null })}
            />
          )}
          {tab === 'fields' && can('fields.view') && (
            <FieldsPage
              openId={url.field}
              onOpenChange={(id) => setUrl({ field: id })}
              // Crossing to the option review closes the field drawer behind it, same as
              // the task-to-decision link: a leftover `field` would reopen this drawer on
              // the way back and `tabForUrl` would read it as the Fields tab.
              onOpenPicklist={(id) => setUrl({ picklist: id, field: null })}
            />
          )}
          {tab === 'picklists' && can('fields.view') && (
            <PicklistsPage
              openId={url.picklist}
              onOpenChange={(id) => setUrl({ picklist: id })}
              // The return half of the Field Guide's link here — and the same rule:
              // crossing closes the drawer behind it, so back does not reopen it.
              onOpenField={(id) => setUrl({ field: id, picklist: null })}
            />
          )}
          {tab === 'products' && can('products.view') && (
            <ProductsPage openId={url.product} onOpenChange={(id) => setUrl({ product: id })} />
          )}
          {tab === 'messages' && (
            <MessagesPage
              openId={url.message}
              onOpenChange={(id) => setUrl({ message: id })}
              // A conversation on an item opens that item on its own tab, at the comment
              // that was clicked — the same address a notification uses.
              onNavigate={setUrl}
            />
          )}
          {tab === 'activity' && <ActivityHistoryPage onNavigate={setUrl} />}
          {tab === 'admin' && isAdmin && <AdminPage />}
          </Suspense>
          </ErrorBoundary>
        </ScopedTabs>
      </main>

      <footer className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-3 gap-y-1 px-3 pb-8 text-xs text-mc-muted sm:px-6">
        <span>Prepared by {PROJECT.deliveredBy} for {PROJECT.client}.</span>
        <span
          className="ml-auto font-mono"
          title={`Branch ${BUILD.branch}\nCommit ${BUILD.sha}${BUILD.dirty ? ' (uncommitted changes)' : ''}\nBuilt ${buildDate()}`}
        >
          {shortLabel()}
        </span>
        <span className="font-mono">built {buildDate()}</span>
      </footer>
    </div>
  )
}
